/* eslint sort-keys: "error" */

import { rem, Box, Text, Link } from '@vimeo/bokeh';
import { Optional } from '@vimeo/core/types/generic';
import { translate } from '@vimeo/i18n-runtime';
import { EGraphicsDescriptorType } from '@vimeo/live-core/types/graphics';
import { EChatType } from '@vimeo/live-core/types/interaction_tools';

export const broadcasterTranslations = {
  active: translate('Active'),
  add: translate('Add'),
  addAnImage: translate('Add an image'),
  addAnySpeakersOrContent: translate(
    'Drag and drop speakers and content from the side panels to build your scene',
  ),
  addAsNewScene: translate('Add as new scene'),
  addFromBrandKit: translate('Add from Brand Kit'),
  addFromGoogleSlides: translate('Add from Google Slides'),
  addImage: translate('Add image'),
  addLiveSegment: translate('Add live segment'),
  addLogo: translate('Add logo'),
  addLowerThird: translate('Add lower third'),
  addLowerThirdDescription: translate(
    'Add a graphic with information about the scene or the speaker',
  ),
  addMedia: translate('Add media'),
  addOn: translate('Add-on'),
  addPoll: translate('Add poll'),
  addPrerecordedVideos: translate('Add prerecorded videos to your scene'),
  addPrimaryText: translate('Add a primary text'),
  addScene: translate('Add scene'),
  addSlides: translate('Add slides'),
  addSpeaker: translate('Add speaker'),
  addSpeakers: translate('Add speakers'),
  addToEvent: translate('Add to event'),
  addToScene: translate('Add to scene'),
  addVideo: translate('Add video'),
  addVideoToSimulatedLiveScenesTextTour: translate(
    'Add videos to the simulated live scenes to automatically go live at the scheduled date and time. Videos in this section will auto transition to the next scene.',
  ),
  addVideos: translate('Add videos'),
  addVideosDescription: translate('Add videos from the Library to use them in your event'),
  addingVideoIsDisabled: translate('Adding video is disabled'),
  adjustAndPreviewTheme: translate('Adjust and Preview Theme'),
  admin: translate('Admin'),
  advanced: translate('Advanced'),
  advancedMarketingIntegrations: translate('Advanced marketing integrations'),
  afterTheVideo: translate(
    'After the video, play the next scene manually or automatically, or let your video loop forever',
  ),
  allLayouts: translate('All layouts'),
  allow: translate('Allow'),
  alreadyStreamingToUrl: translate(
    'You can only stream from one URL at a time. You’re currently streaming to:',
  ),
  analytics: translate('Analytics'),
  approve: translate('Approve'),
  areYouSureToRemoveGuest: (name: string) =>
    translate({
      replacements: {
        NAME: name,
      },
      singular: 'Are you sure you want to remove {NAME} from the event?',
    }),
  askToUnmute: translate('Ask to unmute'),
  attendeeRegistration: translate('Attendee registration'),
  auto: translate('Auto'),
  autoCCLabel: translate('Auto closed captions'),
  autoCCLanguageRequired: translate(
    'Please select a language for automatic closed captions before going live',
  ),
  autoCCNotAvailableWithLowLatencyLive: translate(
    'Automated closed captions are not available for low-latency streaming. To use captions, end your stream and turn off low-latency.',
  ),
  autoCCShortLabel: translate('Auto CC'),
  autoCCUpgradeDefaultTitle: translate('Add auto-captioning to your event'),
  autoCCUpgradeEnterpriseTitle: translate('Your plan has 30 remaining hours for auto-captions'),
  autoCCUpgradeSubscribedTitle: translate('Upgrade for more auto caption time'),
  autoLayout: translate('Auto-layout'),
  autoTransition: translate('Auto transition'),
  backstage: translate('Backstage'),
  backstageModeDescription: translate(
    'When the backstage is closed, all guest speakers can be heard by the audience while the event is live',
  ),
  backstageSettings: translate('Backstage settings'),
  backstageTab: (count: number) =>
    translate({
      replacements: {
        COUNT:
          count === 0 ? (
            ''
          ) : (
            <Text color={'text-secondary'} variant={'body-sm'}>
              &nbsp;{count}
            </Text>
          ),
      },
      singular: 'Backstage {COUNT}',
    }),
  backstageTabOld: (count: number) =>
    translate({
      replacements: {
        COUNT: count,
      },
      singular: 'Backstage ({COUNT})',
    }),

  backstageVolume: translate('Backstage'),
  backupStream: translate('Backup stream'),
  backupStreams: translate('Backup streams'),
  betaGuestsCount: translate(
    'We’re testing an increased speaker limit. Adding more speakers may impact performance while in beta.',
  ),
  brand: translate('Brand'),
  brandedInviteAndReminder: translate('Branded invite & reminder emails'),
  broadcaster: translate('Broadcaster'),
  broadcasterConnectionDownMessage: translate('Sorry, seems like your internet connection is down'),
  broadcasterLowerCase: translate('broadcaster'),
  broadcasterNameLabel: (name: string) =>
    translate({ replacements: { NAME: name }, singular: '{NAME} (broadcaster)' }),
  broadcasting: translate('Broadcasting'),
  buildYourScene: translate('Build your scene'),
  business: translate('Business'),
  camera: translate('Camera'),
  cameras: translate('Cameras'),
  cancelUpload: translate('Cancel upload'),
  cannotFindVideosMessage: translate(
    "We can't find any videos matching this title. Please try again.",
  ),
  captions: translate('Captions'),
  changeColor: translate('Change color'),
  changeLayout: translate('Change layout'),
  changeProductionMode: translate('Change production mode'),
  chat: translate('Chat'),
  chatDisabled: translate('Chat is disabled'),
  chatPlaceholder: {
    [EChatType.PUBLIC]: {
      input: translate('Message audience'),
      text: translate('Welcome your audience by sending a message'),
      title: translate('Chat with your audience'),
    },
    [EChatType.BACKSTAGE]: {
      input: translate('Message speakers'),
      text: translate('Welcome your speakers by sending a message'),
      title: translate('Chat with your speakers'),
    },
  },
  clickHereToStart: translate('Click here to start'),
  clickHereToStream: translate(
    'Click here to stream your event live. You won’t have an audience (yet), but you can copy the link to the stream by clicking the icon in the preview.',
  ),
  closed: translate('Closed'),
  closedCaptions: translate('Closed captions'),
  colors: translate('Colors'),
  comingSoon: translate('Coming soon!'),
  confirm: translate('Confirm'),
  confirmYouAreStillHere: translate('Confirm that you are still here'),
  conflictingItemRemoved: (type: string) =>
    translate({
      replacements: {
        TYPE: type,
      },
      singular:
        'The {TYPE} in this scene was replaced. Each scene can include one video or slide deck.',
    }),
  contactSales: translate('Contact sales'),
  contactUs: translate('Contact us'),
  contentTypeNotSupportedSimulive: translate(
    'This content type is not supported in simulated live scenes',
  ),
  contentWasNotAdded: translate('Content was not added. Your selected layout is full.'),
  controls: translate('Controls'),
  copied: translate('Copied!'),
  copiedBackupStreamKey: translate('Copied Backup stream key'),
  copiedRTMP: translate('Copied RTMP URL'),
  copiedRTMPS: translate('Copied RTMPS URL'),
  copiedStreamKey: translate('Copied stream key'),
  copy: translate('Copy'),
  copyGuestLink: translate('Copy guest link'),
  correctResponse: (response: string) =>
    translate({
      replacements: {
        RESPONSE: response,
      },
      singular: 'Correct response: {RESPONSE}',
    }),
  createPollToInteractWithAudience: translate('Create a poll to survey your audience'),
  customPermissions: translate('Custom permissions'),
  customizeRegistration: translate('Customize registration'),
  customizeRegistrationDetails: translate(
    'Review and make changes to your form or turn off registration',
  ),
  deleteRecording: translate('Delete recording'),
  deleteRecordingAlertNotice: translate('Stop recording and discard'),
  demoEventNotice: translate('This is demo event'),
  disableQna: translate('Disable Q&A'),
  dismiss: translate('Dismiss'),
  dontSupportVodVideos: translate(
    'We don’t support playing back VOD videos. To play this video, you will need to re-upload it.',
  ),
  dontWorryNoLiveYet: translate('Don’t worry - you won’t be live to anyone else ye.'),
  download: translate('Download'),
  dragAndDropLabel: {
    replace: translate('Replace'),
    swap: translate('Swap'),
  },
  dragVideosToAdd: translate('Drag videos from the media panel here to add them'),
  drop: translate('Drop'),
  duplicate: translate('Duplicate'),
  duplicateScene: translate('Duplicate scene'),
  edit: translate('Edit'),
  editAndManageVideo: translate('Edit and manage video'),
  editAndManageVideoUnavailable: translate('Available once your recording finishes processing'),
  editImage: translate('Edit image'),
  editLowerThird: translate('Edit Lower Third'),
  editScene: translate('Edit scene'),
  editSpeaker: translate('Edit speaker'),
  email: translate('Email'),
  emailSent: translate('Email sent'),
  enableAutoClosedCaptions: translate('Enable auto closed captions'),
  enableChat: translate('Enable chat'),
  enableQna: translate('Enable Q&A'),
  end: translate('End'),
  endEvent: translate('End event'),
  ended: translate('Ended'),
  enhancedSpeechDetection: translate('Enhanced speech detection'),
  enterprise: translate('Enterprise'),
  errorCouldNotFindInteractionRoom: translate('Could not find interaction tools room'),
  errorRoomJoinFailed: translate('Failed to join the room'),
  errorSendEmailLimit: translate(
    "You've exceeded your  invite email limit. Take a break & try again soon.",
  ),
  errorSomethingWentWrong: translate('Something went wrong. Please try again later.'),
  errorTechnicalDifficulties: translate('Technical Difficulties'),
  errorTechnicalTryAgain: translate(
    'Whoops! We are having some technical difficulties, please try again in a minute',
  ),
  eventBrowserMode: translate('This event started in Browser mode'),
  eventEnded: translate('This event is ended'),
  eventPrivateToMeFolderWarning: translate('To go live, move this event into a shared folder'),
  eventRtmpMode: translate('This event started in RTMP mode'),
  events: translate('Events'),
  eventsAreUnavailable: translate(
    'This experience is not available on mobile. To join this event, open your invite link on a computer.',
  ),
  exportQuestions: translate('Export questions'),
  exportSession: translate('Export session'),
  fadeBetweenScenes: translate('New: Fade between scenes'),
  fadeBetweenScenesDescription: translate(
    'To improve the audience experience, your content will now fade from one scene to the next whenever you send a scene to stream.',
  ),
  feelFreeToKeepExploring: translate(
    'Feel free to keep exploring our product in this event, or return to the homepage',
  ),
  fileTooLarge: translate('File too large'),
  finish: translate('Finish'),
  font: translate('Font'),
  generateInviteLink: translate('Generate Invite Link'),
  getAdvanced: translate('Get Advanced'),
  getPremium: translate('Get Premium'),
  goLive: translate('Go live'),
  goLiveAgain: translate('Go live again'),
  goLiveAndAutoRecord: translate('Go live and auto-record'),
  graphicAddedToScene: (graphicType: string) =>
    translate({
      replacements: {
        GRAPHIC_TYPE: graphicType,
      },
      singular: 'Added {GRAPHIC_TYPE} to scene',
    }),
  graphicAlreadyInScene: (graphicType: string) =>
    translate({
      replacements: {
        GRAPHIC_TYPE: graphicType,
      },
      singular: 'The {GRAPHIC_TYPE} is already in scene',
    }),
  graphicConflictCancelled: translate('Graphic conflict cancelled'),
  graphicNoMoreSpeakers: (count: number) =>
    translate({
      replacements: {
        COUNT: count,
      },
      singular: 'Speaker was not added. You can add a maximum of {COUNT} speakers per scene.',
    }),
  graphicNoPlaceFound: translate(
    'There is no more room for items in this scene. Switch to auto-layout or a different layout to add this item.',
  ),
  graphicReplacers: {
    image: translate('image'),
    logo: translate('logo'),
    lowerThird: translate('lower third'),
    poll: translate('poll'),
    qna: translate('Q&A'),
    screenshare: translate('screenshare'),
    slides: translate('slides'),
    speaker: translate('speaker'),
    video: translate('video'),
  },
  graphics: translate('Graphics'),
  graphicsChangesWarning: translate(
    'Changes made will be applied to all existing and new graphics',
  ),
  graphicsReplaceAllConfirmation: (itemInsert: string) =>
    translate({
      replacements: {
        ITEM_INSERT: itemInsert,
      },
      singular: 'Replace scene content with {ITEM_INSERT}?',
    }),
  graphicsReplaceConfirmation: (itemRemove: string, itemInsert: string) =>
    translate({
      replacements: {
        ITEM_INSERT: itemInsert,
        ITEM_REMOVE: itemRemove,
      },
      singular: 'Replace {ITEM_REMOVE} with {ITEM_INSERT}?',
    }),
  graphicsReplaceNewConfirmation: (itemRemove: string) =>
    translate({
      replacements: {
        ITEM_REMOVE: itemRemove,
      },
      singular: 'Replace {ITEM_REMOVE} with new graphic?',
    }),
  guaranteedUptime: translate('99.9% guaranteed uptime SLAs'),
  guestLink: translate('Guest link'),
  guestScreenShareRequest: (guestName: string) =>
    translate({
      replacements: {
        GUEST_NAME: guestName,
      },
      singular: '{GUEST_NAME} would like to screen share',
    }),
  hasEnded: (eventTitle: string) =>
    translate({
      replacements: {
        EVENT_TITLE: eventTitle,
      },
      singular: '{EVENT_TITLE} has ended',
    }),
  hasStoppedStreaming: (eventTitle: string) =>
    translate({
      replacements: {
        EVENT_TITLE: eventTitle,
      },
      singular: '{EVENT_TITLE} has stopped streaming',
    }),
  hide: translate('Hide'),
  hideFromScene: translate('Hide from scene'),
  hideLowerThirds: translate('Hide lower thirds'),
  hidePoll: translate('Hide Poll'),
  hideQna: translate('Hide Q&A'),
  hideQuestion: translate('Hide question'),
  hideSceneLowerThird: translate('Hide scene lower third'),
  hideSpeakers: translate('Hide speakers'),
  howToAddSpeaker: translate('How to add a speaker'),
  image: translate('Image'),
  images: translate('Images'),
  imagesMustBeSmaller: translate('Images must be smaller'),
  imagesWillAppearBehindSpeakers: translate('Images will appear behind speakers'),
  inOrderToAccessFailSafeStreaming: (url: string) =>
    translate({
      replacements: {
        LINK: (children: string) => (
          <Link
            key={'link'}
            href={url}
            target={'_blank'}
            variant={'brand'}
            sx={{
              fontSize: rem(14),
            }}
          >
            {children}
          </Link>
        ),
      },
      singular:
        'In order to access our fail-safe streaming offering with your encoder, install the software below. {LINK}Learn more{/LINK}',
    }),
  insufficientAiCreditsForCaptions: translate(
    'Not enough AI credits for captions. Contact your Account Manager to increase your credits.',
  ),
  interactiveTour: translate('Take the Interactive Tour'),
  joinFromComputer: translate('Join from a computer'),
  language: translate('Language'),
  layout: translate('Layout'),
  layouts: translate('Layouts'),
  learnHowToEasilyProduce: translate(
    'Learn how to easily produce your own live event in our hands-on walkthrough',
  ),
  learnMore: translate('Learn more'),
  live: translate('Live'),
  liveDropdownDescription: translate('Manage your production from the web'),
  liveDropdownTitle: translate('Stream from your browser'),
  liveQnaGraphicsAndPolls: translate('Live Q&A, graphics and polls'),
  liveQnaPollsChats: translate('Live Q&A, polls, chats'),
  liveStreamToMultipleDestinations: translate('Live stream to multiple destinations'),
  liveStreamWithConfidence: translate('Live stream with confidence'),
  liveToBeginIn: (timeLabel: string | number, color: string) =>
    translate({
      replacements: {
        TIME: (
          <Box as={'span'} color={color}>
            {timeLabel}
          </Box>
        ),
      },
      singular: 'Live to begin in {TIME}',
    }),

  // `Live to begin in ${timeLabel}`
  logo: translate('Logo'),
  lowerThirds: translate('Lower thirds'),
  manageProduction: translate('Manage production'),
  manageVideo: translate('Manage video'),
  manualLiveInfo: translate(
    'Prerecorded scenes will automatically transition into the first scene of the manual live segment',
  ),
  marketingIntegrations: translate('Marketing integrations'),
  meNameLabel: (name: string) =>
    translate({ replacements: { NAME: name }, singular: '{NAME} (me)' }),
  media: translate('Media'),
  mediaCameraSettings: translate('Camera settings'),
  mediaMicrophoneSettings: translate('Microphone settings'),
  mediaSettings: translate('Video and audio settings'),
  mediaVolume: translate('Media volume'),
  microphone: translate('Microphone'),
  missingPaymentMethodForCaptions: translate(
    'You need to set a default payment method to go live. Additional costs may apply based on credits consumed.',
  ),
  moderateYourQnaSession: translate('Moderate your Q&A session with a custom enterprise plan'),
  moderatedQna: translate('Moderated live Q&A'),
  modifiedTemplate: (dateString: string) =>
    translate({
      replacements: {
        DATE_STRING: dateString,
      },
      singular: 'Modified {DATE_STRING}',
    }),
  name: translate('Name'),
  new: translate('new'),
  newNowYouCanAddPresentations: translate(
    'Now you can add Powerpoints or PDF slideshows to your event',
  ),
  newUploadPresentations: translate('New: upload presentations'),
  next: translate('Next'),
  noContentInStream: translate('No content in stream'),
  noGraphics: translate('No graphics'),
  noSpeakersInTheBackstage: translate('There are no speakers backstage'),
  noSpeakersOnStage: translate('There are no speakers on stage'),
  noStreamTimeRemaining: translate('You have no stream time remaining.'),
  noUploadedVideosMessage: () =>
    translate({
      replacements: {
        LINK: (children: string) => (
          <Link href={'/upload'} target={'_blank'} key={'no-uploaded-videos-link'}>
            {children}
          </Link>
        ),
      },
      singular:
        'It looks like you haven’t uploaded any videos. No problem: Just {LINK}upload new videos{/LINK} to add them to your event.',
    }),
  noneSelected: translate('None selected'),
  notNow: translate('Not now'),
  offCamera: translate('Off camera'),
  onCamera: translate('On camera'),
  onStage: translate('On stage'),
  open: translate('Open'),
  openActiveStream: translate('Open active stream'),
  optional: translate('optional'),
  outputDevice: translate('Output device'),
  overlays: translate('Overlays'),
  pause: translate('Pause'),
  pauseRecording: translate('Pause recording'),
  pauseVideo: translate('Pause video'),
  pending: translate('Pending'),
  placeContent: translate('Place content'),
  play: translate('Play'),
  playOnceAndStop: translate('Play once and stop'),
  playVideo: translate('Play video'),
  player: translate('Player'),
  pleaseEnterAQuestion: translate('Please enter a question'),
  polls: translate('Polls'),
  premium: translate('Premium'),
  prepareRecordingToShare: translate('Prepare your recording to share'),
  prerecordedScenesGoLiveAutomatically: translate(
    'Prerecorded scenes go live automatically at a scheduled date',
  ),
  pressEnterToAdd: translate('Press enter to add keywords or separate by commas'),
  preview: translate('Preview'),
  primaryColor: translate('Primary color'),
  primaryText: translate('Primary text'),
  primaryTextOptional: translate('Primary text (optional)'),
  privateStreamingOnShowcases: translate('Private streaming on Showcases'),
  productionMode: translate('Production mode'),
  productionServicesAndAdvancesSupport: translate('Production services and advanced support'),
  progressToNextScene: translate('Progress to the next scene when the video ends'),
  qna: translate('Q&A'),
  qnaModeration: translate('Q&A moderation'),
  questionIsTooLong: translate('Question is too long'),
  questions: translate('Questions'),
  realtimeStreamHealth: translate('Real-time stream health'),
  recordEvent: translate('Record event'),
  recordWithoutGoingLive: translate('Record without going live'),
  recording: translate('Recording'),
  recordingDiscardNotice: translate('The current recording will be discarded'),
  recordingDropdownDescription: translate('Record a video without an audience'),
  recordingDropdownTitle: translate('Recording only'),
  remove: translate('Remove'),
  removeAllSceneBodyMessageLive: translate(
    'The design and content of all existing scenes will be deleted and replaced',
  ),
  removeAllSceneBodyMessageScheduled: translate(
    'The design and content of all existing live and simulated live scenes will be deleted. Template scenes will be added.',
  ),
  removeFromEvent: translate('Remove from event'),
  removeFromScene: translate('Remove from scene'),
  removeGuestSpeaker: translate('Remove guest speaker from event?'),
  removeScene: translate('Remove scene'),
  replace: translate('Replace'),
  replaceAllScenes: translate('Replace all existing scenes'),
  replaceScenes: translate('Replace scenes'),
  replaceUpload: translate('Replace upload'),
  requestDemo: translate('Request Demo'),
  reset: translate('Reset'),
  restartVideoAfterEnds: translate('Restart the video after it ends'),
  resumeRecording: translate('Resume recording'),
  returnToHomePage: translate('Return to home page'),
  returnToStage: translate('Return to Stage'),
  save: translate('Save'),
  scene: translate('Scene'),
  /**
   * Do not translate since tour is using EN locale (videos).
   * Keys are bound to order because using numbers in text.
   */
  sceneCreation: {
    fifth: '5. Audience interaction',
    first: '1. Introduction',
    fourth: '4. Layouts',
    second: '2. Add speakers',
    seventh: '7. Thank you',
    sixth: '6. Send to stream vs Start event',
    third: '3. Videos',
  },
  scenes: translate('Scenes'),
  screenShareDeniedAccess: translate('Your browser doesn’t have permissions to use screen share'),
  screenShareRequest: translate('Request screen share'),
  screenShareStart: translate('Start screen share'),
  screenShareStop: translate('Stop screen share'),
  searchVideosMessage: () =>
    translate({
      replacements: {
        LINK: (children: string) => (
          <Link href={'/upload'} target={'_blank'} key={'search-videos-link'}>
            {children}
          </Link>
        ),
      },
      singular:
        'Search and add videos to your event. You can also {LINK}upload new videos{/LINK}, then add them here.',
    }),
  secondaryColor: translate('Secondary color'),
  secondaryText: translate('Secondary text'),
  secondaryTextOptional: translate('Secondary text (optional)'),
  seePlans: translate('See plans'),
  selectADevice: translate('Select a device'),
  selectCamera: translate('Select a camera'),
  selectMicrophone: translate('Select a microphone'),
  selectScene: translate('Select scene'),
  selectSpeaker: translate('Select a speaker'),
  selectVideos: translate('Select videos'),
  selectedVideosCount: (count: number) =>
    translate({
      count,
      plural: '{COUNT} selected videos',
      replacements: {
        COUNT: count,
      },
      singular: '{COUNT} selected video',
    }),
  sendInviteViaEmail: translate('Send invite via email'),
  sendLive: translate('Send live'),
  sendToStage: translate('Send to stage'),
  sendToStream: translate('Send to stream'),
  setAsBroadcaster: translate('Set as broadcaster'),
  sharingScreen: translate('Sharing screen'),
  show: translate('Show'),
  showAll: translate('Show all'),
  showLogoOnAllScenes: translate('Show logo on all scenes'),
  showLowerThirds: translate('Show lower thirds'),
  showOnScene: translate('Show on scene'),
  showPoll: translate('Show Poll'),
  showQna: translate('Show Q&A'),
  showQuestion: translate('Show question'),
  showSceneLowerThird: translate('Show scene lower third'),
  showSpeakers: translate('Show speakers'),
  showTheVideoThumbnail: translate('Show the video thumbnail after the video ends'),
  simulatedLiveEventsWillTour: translate(
    'Simulated live events will automatically go live with pre-recorded videos at the selected time and date',
  ),
  simulatedLiveInfo: translate(
    'Prerecorded scenes will automatically play and transition to the next scene at the selected time and date',
  ),
  simulatedLiveScenes: translate('Simulated live scenes'),
  simulive: translate('Simulive'),
  simuliveInfo: translate(
    'Pre-recorded videos will automatically play and transition to the next scene at the selected time and date',
  ),
  simulivePrerecorded: translate('Prerecorded'),
  simuliveTitle: translate('Prerecorded event'),
  simultaneousStreamLimitExceededButtonWarning: (limit: number) =>
    translate({
      replacements: {
        LIMIT: limit,
      },
      singular:
        'You can only have up to {LIMIT} concurrent streams. To go live with this stream, end another stream or contact us to increase your limit.',
    }),
  simultaneousStreamLimitExceededButtonWarningVpaas: (limit: number) =>
    translate({
      replacements: {
        LIMIT: limit,
      },
      singular:
        'You can only have up to {LIMIT} concurrent streams. To go live with this stream, end another stream.',
    }),
  simultaneousStreamLimitExceededContributor: (limit: number) =>
    translate({
      replacements: {
        LIMIT: limit,
      },
      singular: 'You can stream up to {LIMIT} events at once. End a stream or contact your admin.',
    }),
  simultaneousStreamLimitExceededNew: (limit: number) =>
    translate({
      replacements: {
        LIMIT: limit,
      },
      singular: 'You can stream up to {LIMIT} events at once. Get in touch to increase your limit.',
    }),
  singleSso: translate('Single sign-on (SSO)'),
  skip: translate('Skip'),
  slides: translate('Slides'),
  slidesConvertationMessage: translate(
    'The selected file will be converted to static slides. Any animations or effects will be removed, and videos will not be playable.',
  ),
  slidesGoogleImportError: translate(
    'Something went wrong importing from Google Slides. Please try again.',
  ),
  slidesGoogleTooLarge: translate(
    'This deck is too large to import. Try exporting it as a PDF and uploading the PDF instead.',
  ),
  slidesLowercase: translate('slides'),
  somethingWentWrongUploading: translate('Something went wrong uploading...'),
  speaker: translate('Speaker'),
  speakers: translate('Speakers'),
  ssoSkim: translate('SSO (SAML) & SCIM (OKTA, AZURE)'),
  stageTab: (count: number) =>
    translate({
      replacements: {
        COUNT:
          count === 0 ? (
            ''
          ) : (
            <Text color={'text-secondary'} variant={'body-sm'}>
              &nbsp;{count}
            </Text>
          ),
      },
      singular: 'On stage {COUNT}',
    }),
  standard: translate('Standard'),
  start: translate('Start'),
  startEvent: translate('Start event'),
  startEventManually: translate('Start event manually when you are ready'),
  startOver: translate('Start over'),
  startRecording: translate('Record'),
  startWithOurTour: translate('Start with our interactive tour'),
  startsAtDate: (date: Optional<string>) =>
    translate({
      replacements: {
        DATE: date || '?',
      },
      singular: 'Starts {DATE}',
    }),
  startsAutomaticallyWith: translate('Go live automatically with a prerecorded segment'),
  stop: translate('Stop'),
  stopRecordingAlertTitle: translate('Stop recording'),
  stopScreenSharing: translate('Stop screen sharing'),
  stopSharing: translate('Stop sharing'),
  stream: translate('Stream'),
  streamAgain: translate('Stream again'),
  streamPreview: translate('Stream preview'),
  streamTimeRemaining: (minutes: string, seconds: string) =>
    translate({
      replacements: {
        MINUTES: minutes,
        SECONDS: seconds,
      },
      singular: '{MINUTES}:{SECONDS} stream time remaining',
    }),
  streamVolume: translate('Stream'),
  streaming: translate('Streaming'),
  swap: translate('Swap'),
  switchCamera: translate('Switch camera'),
  switchToSimulatedLive: translate('Switch to simulated live'),
  switchToYou: translate('Switch to you'),
  takeOverGuestScreenShare: translate('Take over guest screen share?'),
  takeTheTour: translate('Take the tour'),
  teleprompterButtonDemoTooltip: translate('Teleprompter is not available in demo mode'),
  teleprompterClose: translate('Close teleprompter'),
  teleprompterOpen: translate('Open teleprompter'),
  templates: translate('Templates'),
  templatesAudioTourBody: translate(
    'Music will play as you send scenes to the stream. If you prefer, you can adjust the media volume to mute the sound.',
  ),
  templatesAudioTourTitle: translate('Templates now have music'),
  thanksForTakingOurTour: translate('Thanks for taking our tour!'),
  theRecording: translate(
    'Your recording will be available soon. Review your settings and get your video ready to share.',
  ),
  theRecordingOfYourEvent: translate(
    'The recording of your event will be available soon. Review your settings and get your video ready to share.',
  ),
  thisEventHasBeenAdded: translate('This event has been added to your account'),
  thisVideoIsOptimizing: translate('This video is optimizing. Check back later.'),
  title: translate('Title'),

  toGoLiveUpgradeToPaidPlan: translate('To go live to an audience, upgrade to a paid plan'),
  toPlaySendSceneToStream: translate('To play a video, send the scene to stream'),
  toPlaySendToStream: translate('To play a video, send it to stream'),
  toPresentTheseSlidesWithSpeaker: translate(
    'To present these slides with a speaker\'s audio or video, click the "Speakers" panel and add a speaker to this scene',
  ),
  tooManyEventsCreated: translate(
    'Too many events have been created. Wait a few minutes and try again.',
  ),
  tryIt: translate('Try it'),
  tryReducingPdfFileSize: translate('Try reducing the file size by removing content from your pdf'),
  tryReducingPptFileSize: translate(
    'Try reducing the file size by removing animations, video, or transitions from the presentation',
  ),
  turnOffChat: translate('Turn off chat'),
  unexpectedError: translate('Ooops, something unexpected happened...'),
  unlimitedLiveEvents: translate('Unlimited live events'),
  unlimitedVideos: translate('Unlimited videos'),
  unscheduled: translate('Unscheduled'),
  unsupportedBrowser: translate('Unsupported Browser'),
  unsupportedBrowserVendor: (browserList: string) =>
    translate({
      replacements: {
        BROWSERS_LIST: browserList,
      },
      singular:
        'Your browser does not support WebRTC. Compatible browsers include: {BROWSERS_LIST}.',
    }),
  unsupportedBrowserVersionUpgrade: (browserName: string, browserVersion: string) =>
    translate({
      replacements: {
        BROWSER_NAME: browserName,
        VERSION_NUMBER: browserVersion,
      },
      singular:
        'Your current version of {BROWSER_NAME} does not support WebRTC. Please upgrade to a minimum of {BROWSER_NAME} v{VERSION_NUMBER} or use a different browser.',
    }),
  upgrade: translate('Upgrade'),
  upgradeToGoLive: translate('Upgrade to go live'),
  upgradeToGoLiveDescription: translate(
    'This is a demo event. Upgrade to host your next event with Vimeo.',
  ),
  upgradeToModerateYourQna: translate('Upgrade to moderate your Q&A'),
  upgradeToModerateYourQnaDescription: translate(
    'Q&A moderation lets you take control of the discussion. Review and approve questions before they’re shown to your audience.',
  ),
  upload: translate('Upload'),
  uploadFailed: translate('Upload failed. Please try again.'),
  uploadFileAsSlides: translate('Upload file as slides'),
  uploadImagesToEvent: translate('Upload images to add to your event'),
  uploadInProgress: translate('Upload in progress. This may take a few minutes.'),
  uploadSlidesToEvent: translate(
    'Upload slides to present. PDF and PPTX files up to 100MB are supported.',
  ),
  uploaded: translate('Uploaded'),
  uploading: translate('Uploading'),
  useAllCountScenes: (count: number | string) =>
    translate({
      replacements: {
        COUNT: count,
      },
      singular: 'Use all {COUNT} scenes',
    }),
  video: translate('Video'),
  videoBehavior: translate('Video behavior'),
  videoCantBePlayedInEditMode: translate('Video can not be played in edit mode'),
  videoComplete: translate('Complete!!!!'),
  videoFailed: translate('Failed!'),
  videoLoopSelectDescriptionAuto: translate('Progress to the next scene when the video ends'),
  videoLoopSelectDescriptionLoop: translate('Restart the video after it ends'),
  videoLoopSelectDescriptionManual: translate('Show the video thumbnail after the video ends'),
  videoLoopSelectTitleAuto: translate('Auto'),
  videoLoopSelectTitleLoop: translate('Loop'),
  videoLoopSelectTitleManual: translate('Manual'),
  videoOptimizationPending: translate('Optimization pending...'),
  videoOptimizing: translate('Optimizing...'),
  videoPlaybackOptions: translate('Video playback options'),
  videos: translate('Videos'),
  viewAll: translate('View all'),
  viewGuestSources: translate('View guest sources'),
  viewRecording: translate('Review settings, view your recording, and trim unwanted sections'),
  viewing: translate('Viewing'),
  volume: translate('Volume'),
  yesRemove: translate('Yes, remove'),
  you: translate('You'),
  youCannotChangeLayoutWithMedia: translate(
    'You cannot change the layout while playing a video. To update the layout, remove the video from the scene.',
  ),
  youCannotChangeLayoutWithScreen: translate(
    'You cannot change the layout while someone is sharing their screen',
  ),
  youHaveAnotherActiveStream: translate('You have another active stream'),
  youHaveReachedLimitOfScenes: translate(
    'You have reached the maximum number of scenes that can be added to an event',
  ),
  yourEventStopped: translate('Your event has stopped streaming'),
  yourPlan: translate('Your plan'),
  yourStreamHasStopped: translate('Your stream has stopped'),
  yourStreamUploaded: translate(
    'Your stream is being uploaded to Vimeo. We’ll email you a link when it’s ready!',
  ),
  yourVoiceIsRecorded: translate('Your voice is being recorded'),
};

export const NormalizedGraphicsDescriptorType: Record<EGraphicsDescriptorType, string> = {
  [EGraphicsDescriptorType.LOWER_THIRD]: broadcasterTranslations.graphicReplacers.lowerThird,
  [EGraphicsDescriptorType.BROADCASTER_LOWER_THIRD]:
    broadcasterTranslations.graphicReplacers.lowerThird,
  [EGraphicsDescriptorType.GUEST_LOWER_THIRD]: broadcasterTranslations.graphicReplacers.lowerThird,
  [EGraphicsDescriptorType.POLL]: broadcasterTranslations.graphicReplacers.poll,
  [EGraphicsDescriptorType.QNA]: broadcasterTranslations.graphicReplacers.qna,
  [EGraphicsDescriptorType.LOGO]: broadcasterTranslations.graphicReplacers.logo,
  [EGraphicsDescriptorType.IMAGE]: broadcasterTranslations.graphicReplacers.image,
  [EGraphicsDescriptorType.AGORA_STREAM]: broadcasterTranslations.graphicReplacers.speaker,
  [EGraphicsDescriptorType.AGORA_SCREEN]: broadcasterTranslations.graphicReplacers.screenshare,
  [EGraphicsDescriptorType.AGORA_MEDIA]: broadcasterTranslations.graphicReplacers.video,
  [EGraphicsDescriptorType.SLIDES]: broadcasterTranslations.graphicReplacers.slides,
};
